/**
 * SHAPExplanationPanel Component
 * Displays SHAP-based feature explanations in a technician-friendly format
 * Shows which sensor readings are contributing to anomaly predictions
 */

import { useMemo, useState } from 'react'
import Plot from 'react-plotly.js'
import {
  Lightbulb,
  ChevronDown,
  Thermometer,
  Gauge,
  Activity,
  AlertCircle,
  HelpCircle,
  TrendingUp,
  TrendingDown
} from 'lucide-react'
import clsx from 'clsx'

interface FeatureContribution {
  feature: string
  value: number
  shap_value: number
  unit?: string
  normal_range?: { min: number; max: number }
  category?: 'temperature' | 'pressure' | 'vibration' | 'flow' | 'composition' | 'other'
}

interface SHAPExplanationPanelProps {
  assetId: string
  baseValue: number
  outputValue: number
  contributions: FeatureContribution[]
  maxFeatures?: number
  showWaterfall?: boolean
  showTechnicianGuide?: boolean
  className?: string
}

export default function SHAPExplanationPanel({
  assetId,
  baseValue,
  outputValue,
  contributions,
  maxFeatures = 10,
  showWaterfall = true,
  showTechnicianGuide = true,
  className
}: SHAPExplanationPanelProps) {
  const [expandedFeature, setExpandedFeature] = useState<string | null>(null)
  const [showAllFeatures, setShowAllFeatures] = useState(false)

  // Sort by absolute SHAP value
  const sortedContributions = useMemo(() => {
    return [...contributions]
      .sort((a, b) => Math.abs(b.shap_value) - Math.abs(a.shap_value))
  }, [contributions])

  const displayedContributions = showAllFeatures
    ? sortedContributions
    : sortedContributions.slice(0, maxFeatures)

  // Separate positive and negative contributions
  const positiveContributions = sortedContributions.filter(c => c.shap_value > 0)
  const negativeContributions = sortedContributions.filter(c => c.shap_value < 0)

  // Generate waterfall chart data
  const waterfallData = useMemo(() => {
    const top = sortedContributions.slice(0, 8)
    const otherPositive = sortedContributions.slice(8).filter(c => c.shap_value > 0).reduce((sum, c) => sum + c.shap_value, 0)
    const otherNegative = sortedContributions.slice(8).filter(c => c.shap_value < 0).reduce((sum, c) => sum + c.shap_value, 0)

    const labels = ['Base Value', ...top.map(c => c.feature.replace(/_/g, ' ').slice(0, 25))]
    const values = [baseValue, ...top.map(c => c.shap_value)]

    if (otherPositive > 0.01) {
      labels.push('Other (+)')
      values.push(otherPositive)
    }
    if (otherNegative < -0.01) {
      labels.push('Other (-)')
      values.push(otherNegative)
    }
    labels.push('Final Score')

    return { labels, values }
  }, [sortedContributions, baseValue])

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case 'temperature': return <Thermometer size={14} className="text-orange-400" />
      case 'pressure': return <Gauge size={14} className="text-blue-400" />
      case 'vibration': return <Activity size={14} className="text-purple-400" />
      default: return <AlertCircle size={14} className="text-surface-400" />
    }
  }

  const isOutOfRange = (contrib: FeatureContribution) => {
    if (!contrib.normal_range) return false
    return contrib.value < contrib.normal_range.min || contrib.value > contrib.normal_range.max
  }

  const getContributionColor = (shap: number) => {
    if (shap > 0.1) return 'text-status-critical'
    if (shap > 0.05) return 'text-status-warning'
    if (shap < -0.1) return 'text-status-healthy'
    if (shap < -0.05) return 'text-green-400'
    return 'text-surface-400'
  }

  const getTechnicianAdvice = (contrib: FeatureContribution): string => {
    const isHigh = contrib.shap_value > 0.1

    if (contrib.feature.toLowerCase().includes('temp')) {
      return isHigh
        ? 'Elevated temperature detected. Check cooling system, ambient conditions, and valve operation.'
        : 'Temperature within normal operating range. No action required.'
    }
    if (contrib.feature.toLowerCase().includes('pressure')) {
      return isHigh
        ? 'Pressure deviation detected. Inspect relief valves, check for blockages, verify gauge accuracy.'
        : 'Pressure readings normal. Continue monitoring.'
    }
    if (contrib.feature.toLowerCase().includes('vibration') || contrib.feature.toLowerCase().includes('harmonic')) {
      return isHigh
        ? 'Vibration levels elevated. Check for loose components, bearing wear, or misalignment.'
        : 'Vibration within acceptable limits.'
    }
    return isHigh
      ? 'This sensor is contributing to anomaly detection. Verify sensor calibration and physical conditions.'
      : 'Reading within expected parameters.'
  }

  return (
    <div className={clsx('rounded-xl border border-surface-700 bg-surface-900 overflow-hidden', className)}>
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-yellow-500/10 to-transparent border-b border-surface-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-500/20 text-yellow-500">
              <Lightbulb size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-surface-100">AI Explanation</h3>
              <p className="text-xs text-surface-400">
                Why the model made this prediction for <span className="font-mono text-primary-400">{assetId}</span>
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-surface-400">Final Score</p>
            <p className={clsx(
              'text-xl font-bold font-mono',
              outputValue >= 0.5 ? 'text-status-critical' : 'text-status-healthy'
            )}>
              {(outputValue * 100).toFixed(1)}%
            </p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="p-4 grid grid-cols-3 gap-4 bg-surface-800/30 border-b border-surface-700">
        <div className="text-center">
          <p className="text-2xl font-bold text-status-critical">{positiveContributions.length}</p>
          <p className="text-xs text-surface-400">Factors Increasing Risk</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-status-healthy">{negativeContributions.length}</p>
          <p className="text-xs text-surface-400">Factors Decreasing Risk</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-surface-300">{contributions.length}</p>
          <p className="text-xs text-surface-400">Total Sensors Analyzed</p>
        </div>
      </div>

      {/* Waterfall Chart */}
      {showWaterfall && (
        <div className="p-4 border-b border-surface-700">
          <h4 className="text-xs font-semibold text-surface-400 uppercase mb-3">Contribution Breakdown</h4>
          <Plot
            data={[{
              type: 'waterfall' as const,
              orientation: 'h' as const,
              measure: ['absolute', ...waterfallData.values.slice(1, -1).map(() => 'relative'), 'total'],
              y: waterfallData.labels,
              x: waterfallData.values,
              connector: { line: { color: '#475569' } },
              increasing: { marker: { color: '#ef4444' } },
              decreasing: { marker: { color: '#10b981' } },
              totals: { marker: { color: '#3b82f6' } },
              textposition: 'outside' as const,
              text: waterfallData.values.map(v => v > 0 ? `+${(v * 100).toFixed(1)}%` : `${(v * 100).toFixed(1)}%`),
              textfont: { size: 10, color: '#94a3b8' }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any]}
            layout={{
              height: 350,
              margin: { t: 20, r: 80, b: 40, l: 180 },
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              font: { color: '#94a3b8', family: 'Inter, sans-serif' },
              xaxis: {
                title: { text: 'Contribution to Anomaly Score' },
                gridcolor: '#334155',
                zerolinecolor: '#475569',
                tickformat: '.0%'
              },
              yaxis: {
                autorange: 'reversed',
                tickfont: { size: 11 }
              },
              showlegend: false
            }}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: '100%' }}
          />
        </div>
      )}

      {/* Feature List */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold text-surface-400 uppercase">Top Contributing Factors</h4>
          <button
            onClick={() => setShowAllFeatures(!showAllFeatures)}
            className="text-xs text-primary-400 hover:text-primary-300"
          >
            {showAllFeatures ? 'Show Less' : `Show All (${contributions.length})`}
          </button>
        </div>

        <div className="space-y-2">
          {displayedContributions.map((contrib, index) => {
            const isExpanded = expandedFeature === contrib.feature
            const outOfRange = isOutOfRange(contrib)

            return (
              <div
                key={contrib.feature}
                className={clsx(
                  'rounded-lg border transition-all',
                  outOfRange ? 'border-status-warning/50 bg-status-warning/5' : 'border-surface-700 bg-surface-800/50',
                  isExpanded && 'ring-1 ring-primary-500/50'
                )}
              >
                <button
                  onClick={() => setExpandedFeature(isExpanded ? null : contrib.feature)}
                  className="w-full p-3 flex items-center gap-3 text-left"
                >
                  {/* Rank */}
                  <span className="text-xs font-mono text-surface-500 w-6">#{index + 1}</span>

                  {/* Category Icon */}
                  {getCategoryIcon(contrib.category)}

                  {/* Feature Name & Value */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-surface-200 truncate">
                        {contrib.feature.replace(/_/g, ' ')}
                      </span>
                      {outOfRange && (
                        <AlertCircle size={12} className="text-status-warning flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-surface-400">Value:</span>
                      <span className="font-mono text-surface-300">
                        {typeof contrib.value === 'number' ? contrib.value.toFixed(2) : contrib.value}
                        {contrib.unit && ` ${contrib.unit}`}
                      </span>
                      {contrib.normal_range && (
                        <span className="text-surface-500">
                          (normal: {contrib.normal_range.min}-{contrib.normal_range.max})
                        </span>
                      )}
                    </div>
                  </div>

                  {/* SHAP Value */}
                  <div className="text-right">
                    <div className={clsx('flex items-center gap-1', getContributionColor(contrib.shap_value))}>
                      {contrib.shap_value > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                      <span className="font-mono font-medium">
                        {contrib.shap_value > 0 ? '+' : ''}{(contrib.shap_value * 100).toFixed(1)}%
                      </span>
                    </div>
                    <span className="text-xs text-surface-500">
                      {contrib.shap_value > 0 ? 'increases' : 'decreases'} risk
                    </span>
                  </div>

                  <ChevronDown
                    size={16}
                    className={clsx('text-surface-500 transition-transform', isExpanded && 'rotate-180')}
                  />
                </button>

                {/* Expanded Details */}
                {isExpanded && showTechnicianGuide && (
                  <div className="px-3 pb-3 border-t border-surface-700/50">
                    <div className="mt-3 p-3 bg-surface-900 rounded-lg">
                      <div className="flex items-start gap-2">
                        <HelpCircle size={14} className="text-primary-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-surface-300 mb-1">Technician Guidance:</p>
                          <p className="text-xs text-surface-400">{getTechnicianAdvice(contrib)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Technician Summary */}
      {showTechnicianGuide && positiveContributions.length > 0 && (
        <div className="p-4 bg-status-warning/5 border-t border-status-warning/20">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-status-warning flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-surface-200 mb-2">Action Summary for Technicians</h4>
              <ul className="space-y-1 text-sm text-surface-300">
                {positiveContributions.slice(0, 3).map(contrib => (
                  <li key={contrib.feature} className="flex items-start gap-2">
                    <span className="text-status-warning mt-1">•</span>
                    <span>
                      <span className="font-medium">{contrib.feature.replace(/_/g, ' ').slice(0, 40)}</span>
                      : Check {contrib.category || 'sensor'} for issues
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
